'use client';

import { useRouter } from 'next/navigation';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { Button } from '@flipster/react/design-system/button';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import segment, { MarketingEvent } from '@flipster/utils/segment';
import { valueof } from '@flipster/utils/typescript';
import { generateLinkWithQueryParams } from '@flipster/utils/url';
import webviewHandler from '@flipster/utils/webview-handler';

type ButtonPosition = 'main' | 'footer';
interface ButtonJson {
  text: string;
  url: string;
  appLocator: string;
}

interface Props {
  section: ButtonPosition;
  defaultJson?: ButtonJson;
  loggedInJson?: ButtonJson;
}

const ButtonPositions: Record<ButtonPosition, valueof<typeof MarketingEvent>> = {
  main: MarketingEvent.MarketingLandingTopClick,
  footer: MarketingEvent.MarketingLandingBottomClick,
};

export const MarketingButton = ({ section, defaultJson, loggedInJson }: Props) => {
  const router = useRouter();

  const isMounted = useMounted();
  const { isCookieLoggedIn } = useAuth();

  let json: ButtonJson | undefined = defaultJson;
  if (isCookieLoggedIn && !!loggedInJson) json = loggedInJson;

  if (!isMounted || !json) return null;

  return (
    <Button
      hierarchy='primary'
      color='accent'
      size='large'
      label={json.text}
      css={{ px: 12 }}
      onClick={() => {
        if (!json) return;

        segment.track(ButtonPositions[section], {
          button_url: json.url,
          button_text: json.text,
        });

        const goLink = () => {
          const url = generateLinkWithQueryParams(json.url, new URLSearchParams(window.location.search));
          const isExternal = url.startsWith('https');
          if (isExternal) window.open(url, '_blank', 'noopener');
          else router.push(url);
        };

        if (json.appLocator) {
          webviewHandler.postMessage(
            {
              action: 'link',
              params: { page: json.appLocator },
            },
            () => goLink(),
          );
          return;
        }
        goLink();
      }}
    />
  );
};
